import { useState } from "react";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, MessageCircle, CheckCircle2 } from "lucide-react";
import { submitContact } from "@/lib/api";
import { useLang } from "@/context/LangContext";

export default function Contact() {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "General Enquiry", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k, v) => setForm({ ...form, [k]: v });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email and message");
      return;
    }
    setSubmitting(true);
    try {
      await submitContact(form);
      setSubmitted(true);
      toast.success("Message sent. We'll be in touch shortly.");
    } catch {
      toast.error("Failed to send. Please try WhatsApp: 9135 1039");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-2 min-h-[calc(100vh-5rem)]" data-testid="contact-page">
      {/* Left dark */}
      <aside className="bg-[#0A1128] text-white grain relative">
        <div className="px-8 sm:px-14 py-20 sm:py-24">
          <div className="overline text-[#688A4E] mb-4">{t("contact.overline")}</div>
          <h1 className="font-serif text-5xl sm:text-6xl leading-tight mb-6">{t("contact.title1")}<br/> {t("contact.title2")}</h1>
          <p className="text-[#A1A9BC] text-lg leading-relaxed max-w-md mb-14">{t("contact.subhead")}</p>

          <ul className="space-y-8">
            <li className="flex gap-4">
              <MapPin className="w-5 h-5 text-[#688A4E] mt-1 shrink-0"/>
              <div>
                <div className="overline text-[#A1A9BC] mb-1">Visit</div>
                <div className="text-white leading-relaxed">304 Orchard Road<br/>#04-07 Lucky Plaza, Singapore 238863</div>
              </div>
            </li>
            <li className="flex gap-4">
              <Phone className="w-5 h-5 text-[#688A4E] mt-1 shrink-0"/>
              <div>
                <div className="overline text-[#A1A9BC] mb-1">Call · WhatsApp</div>
                <a href="tel:+6591351039" className="text-white text-lg hover:text-[#688A4E] transition" data-testid="contact-phone">9135 1039</a>
              </div>
            </li>
            <li className="flex gap-4">
              <Mail className="w-5 h-5 text-[#688A4E] mt-1 shrink-0"/>
              <div>
                <div className="overline text-[#A1A9BC] mb-1">Email</div>
                <a href="mailto:happyhome_agency@yahoo.com.sg" className="text-white hover:text-[#688A4E] transition break-all" data-testid="contact-email">happyhome_agency@yahoo.com.sg</a>
              </div>
            </li>
            <li className="flex gap-4">
              <Clock className="w-5 h-5 text-[#688A4E] mt-1 shrink-0"/>
              <div>
                <div className="overline text-[#A1A9BC] mb-1">Hours</div>
                <div className="text-white leading-relaxed">Mon–Fri · 10am – 7pm<br/>Sat–Sun · 10am – 6pm<br/>Public Holidays · By Appointment</div>
              </div>
            </li>
          </ul>

          <div className="mt-14 pt-8 border-t border-white/10 text-xs text-[#A1A9BC]">
            <div className="mb-1">EA Personnel: Carol · R1113494 · George · R1102140</div>
          </div>

          <a href="https://wa.me/6591351039" target="_blank" rel="noreferrer" data-testid="contact-whatsapp-btn" className="mt-8 inline-flex items-center gap-2 bg-[#688A4E] hover:bg-[#56763E] px-6 py-3 text-white transition">
            <MessageCircle className="w-4 h-4"/> Chat on WhatsApp
          </a>
        </div>
      </aside>

      {/* Right form */}
      <section className="bg-white px-8 sm:px-14 py-20 sm:py-24 flex items-center">
        {submitted ? (
          <div className="w-full text-center" data-testid="contact-success">
            <CheckCircle2 className="w-16 h-16 text-[#688A4E] mx-auto mb-6"/>
            <div className="overline mb-4 justify-center">Message Sent</div>
            <h2 className="font-serif text-4xl text-[#0A1128] mb-4">Thank you, {form.name.split(" ")[0]}.</h2>
            <p className="text-gray-600 mb-8">We'll reply within one business day.</p>
            <button onClick={() => { setSubmitted(false); setForm({ name: "", email: "", phone: "", subject: "General Enquiry", message: "" }); }} className="btn-outline" data-testid="contact-new">Send another</button>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="w-full max-w-lg space-y-6">
            <div className="overline">Send a message</div>
            <h2 className="font-serif text-4xl text-[#0A1128] leading-tight mb-8">We usually reply the same day.</h2>

            <div>
              <label className="block text-xs tracking-widest uppercase text-gray-500 mb-2">Your name *</label>
              <input data-testid="contact-name" type="text" value={form.name} onChange={(e) => update("name", e.target.value)} className="w-full border-b border-gray-300 py-3 bg-transparent focus:outline-none focus:border-[#688A4E]"/>
            </div>
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className="block text-xs tracking-widest uppercase text-gray-500 mb-2">Email *</label>
                <input data-testid="contact-email-input" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className="w-full border-b border-gray-300 py-3 bg-transparent focus:outline-none focus:border-[#688A4E]"/>
              </div>
              <div>
                <label className="block text-xs tracking-widest uppercase text-gray-500 mb-2">Phone</label>
                <input data-testid="contact-phone-input" type="tel" value={form.phone} onChange={(e) => update("phone", e.target.value)} className="w-full border-b border-gray-300 py-3 bg-transparent focus:outline-none focus:border-[#688A4E]"/>
              </div>
            </div>
            <div>
              <label className="block text-xs tracking-widest uppercase text-gray-500 mb-2">Subject</label>
              <select data-testid="contact-subject" value={form.subject} onChange={(e) => update("subject", e.target.value)} className="w-full border-b border-gray-300 py-3 bg-transparent focus:outline-none focus:border-[#688A4E] text-[#0A1128]">
                <option>General Enquiry</option>
                <option>Hire a Helper</option>
                <option>Change Helper</option>
                <option>Employer Care</option>
                <option>Other</option>
              </select>
            </div>
            <div>
              <label className="block text-xs tracking-widest uppercase text-gray-500 mb-2">Message *</label>
              <textarea data-testid="contact-message" rows={5} value={form.message} onChange={(e) => update("message", e.target.value)} className="w-full border border-gray-300 p-4 focus:outline-none focus:border-[#688A4E] bg-transparent"/>
            </div>

            <button type="submit" disabled={submitting} data-testid="contact-submit" className="btn-primary disabled:opacity-60">
              {submitting ? "Sending…" : "Send Message"}
            </button>
          </form>
        )}
      </section>

      {/* Map */}
      <section className="lg:col-span-2 border-t border-gray-200" data-testid="contact-map-section">
        <iframe
          title="Happy Home Agency Location"
          src="https://www.google.com/maps?q=304+Orchard+Road+Lucky+Plaza+Singapore+238863&output=embed"
          width="100%"
          height="400"
          style={{ border: 0 }}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          data-testid="contact-map-iframe"
        />
      </section>
    </div>
  );
}
